import React, { useState } from 'react';
import { Landmark, Info, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Repères généraux du régime algérien d'assurance maladie (CNAS pour les salariés,
// CASNOS pour les non-salariés — même panier de soins de base). Ce n'est pas un
// document officiel : les montants réels dépendent du tarif de référence
// (souvent différent du prix payé), de votre situation, et des conventions en
// vigueur. Vérifiez toujours auprès de la CNAS/CASNOS (numéro vert 30-10) ou de
// votre agence.
const CATEGORIES = [
  { key: 'consultation', labelKey: 'cnas_cat_consultation', rate: 0.8 },
  { key: 'pharmacie', labelKey: 'cnas_cat_pharmacie', rate: 0.8 },
  { key: 'hospitalisation', labelKey: 'cnas_cat_hospitalisation', rate: 0.8 },
  { key: 'chronique', labelKey: 'cnas_cat_chronique', rate: 1 },
  { key: 'maternite', labelKey: 'cnas_cat_maternite', rate: 1 },
];

const CnasReimbursementSimulator = () => {
  const { t } = useLanguage();
  const [categoryKey, setCategoryKey] = useState('consultation');
  const [amount, setAmount] = useState('');

  const category = CATEGORIES.find((c) => c.key === categoryKey);
  const a = parseFloat(amount);
  const valid = a > 0;
  const reimbursed = valid ? a * category.rate : null;
  const remaining = valid ? a - reimbursed : null;

  return (
    <ToolPageLayout
      title={t('cnas_title')}
      subtitle={t('cnas_subtitle')}
      specialty="generaliste"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('cnas_info_pre')}<strong>{t('cnas_info_strong1')}</strong>{t('cnas_info_mid')}<strong>{t('cnas_info_strong2')}</strong>{t('cnas_info_post')}
        </p>
      </div>

      <div className="mb-6">
        <Label className="mb-1 block">{t('cnas_type_label')}</Label>
        <select
          value={categoryKey}
          onChange={(e) => setCategoryKey(e.target.value)}
          className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        >
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{t(c.labelKey)}</option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <Label className="mb-1 block">{t('cnas_amount_label')}</Label>
        <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('cnas_amount_placeholder')} className="rounded-xl max-w-xs" />
      </div>

      {valid && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-[#F0FDF4] border border-[#10B981]/30 rounded-2xl p-5 text-center">
            <p className="text-xs text-[#065F46] mb-1">{t('cnas_reimbursed_label')} ({Math.round(category.rate * 100)} %)</p>
            <p className="text-2xl font-bold text-[#10B981]">{reimbursed.toLocaleString('fr-FR')} DZD</p>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5 text-center">
            <p className="text-xs text-[#64748B] mb-1">{t('cnas_remaining_label')}</p>
            <p className="text-2xl font-bold text-[#0F172A]">{remaining.toLocaleString('fr-FR')} DZD</p>
          </div>
        </div>
      )}

      <div className="mt-8 bg-white border border-[#E2E8F0] rounded-2xl p-5">
        <h3 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
          <Landmark className="w-5 h-5 text-[#0596DE]" /> {t('cnas_chifa_heading')}
        </h3>
        <p className="text-sm text-[#64748B]">
          {t('cnas_chifa_text')}
        </p>
      </div>

      <div className="mt-6 bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-2xl p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#92400E]">
          {t('cnas_disclaimer')}
        </p>
      </div>
    </ToolPageLayout>
  );
};

export default CnasReimbursementSimulator;
